import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../contexts/DashboardContext.jsx';
import StatCard from '../components/StatCard.jsx';
import ChartCard from '../components/ChartCard.jsx';
import CallVolumeChart from '../components/CallVolumeChart.jsx';
import CallDurationChart from '../components/CallDurationChart.jsx';
import ServiceLevelChart from '../components/ServiceLevelChart.jsx';
import AgentStatusChart from '../components/AgentStatusChart.jsx';
import MissedCallsModal from '../components/MissedCallsModal.jsx';
import AgentDetailModal from '../components/AgentDetailModal.jsx';

function DashboardPage() {
  const navigate = useNavigate();
  const {
    data, monitorData, loading, error, lastUpdated, refresh,
    selectedAgent, setSelectedAgent,
    showMissedCalls, setShowMissedCalls
  } = useDashboard();

  const [agentDateRange, setAgentDateRange] = useState('today');
  const stats = data?.stats;

  const getDateRange = (range) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    if (range === 'today') {
      return { start: today, end: today };
    } else if (range === 'current_month') {
      return {
        start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
        end: today
      };
    } else if (range === 'previous_month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        start: `${firstDay.getFullYear()}-${String(firstDay.getMonth() + 1).padStart(2, '0')}-01`,
        end: `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
      };
    }
    return { start: today, end: today };
  };

  if (loading && !data) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '60vh', gap: '12px'
      }}>
        <div style={{
          width: '36px', height: '36px',
          border: '3px solid var(--border)', borderTopColor: 'var(--primary)',
          borderRadius: '50%', animation: 'spin 1s linear infinite'
        }} />
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '60vh', gap: '12px'
      }}>
        <div style={{ color: 'var(--danger)', fontSize: '16px', fontWeight: '600' }}>Error: {error}</div>
        <button onClick={refresh} style={{
          padding: '8px 20px', background: 'var(--primary)', color: 'white',
          border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          fontSize: '14px', fontWeight: '600', fontFamily: 'inherit'
        }}>Retry</button>
      </div>
    );
  }

  const callVolumeData = stats ? [
    { label: 'Answered', value: stats.completedCalls },
    { label: 'Missed', value: stats.missedCallsCount },
    { label: 'Outbound', value: stats.outboundSummary?.reduce((a, b) => a + b.total_calls, 0) || 0 },
    { label: 'Abandoned', value: stats.abandonedCalls }
  ] : [];

  return (
    <div className="animate-fade-in">
      {/* Compact status bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '12px', padding: '8px 12px', background: 'var(--bg-card)',
        borderRadius: 'var(--radius)', border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '7px', height: '7px', borderRadius: '50%',
            background: data ? '#10b981' : '#ef4444'
          }} />
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {lastUpdated ? lastUpdated.toLocaleTimeString('en-ZA') : 'Connecting'}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(30s)</span>
        </div>
        <button onClick={refresh} disabled={loading} style={{
          padding: '4px 10px', background: 'var(--primary-light)', color: 'var(--primary)',
          border: '1px solid var(--primary)', borderRadius: 'var(--radius-sm)',
          cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px',
          fontWeight: '500', fontFamily: 'inherit', opacity: loading ? 0.6 : 1
        }}>
          {loading ? '...' : '↻'}
        </button>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
        <StatCard title="Completed" value={stats?.completedCalls} color="#10b981" />
        <StatCard
          title="Missed"
          value={stats?.missedCallsCount}
          color="#ef4444"
          clickable
          onClick={() => setShowMissedCalls(true)}
        />
        <StatCard title="Talk Time" value={`${stats?.totalTalkTime || 0}m`} color="#0a72e7" />
        <StatCard title="Abandoned" value={stats?.abandonedCalls} color="#f59e0b" />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
        <ChartCard title="Call Volume">
          <CallVolumeChart data={callVolumeData} />
        </ChartCard>
        <ChartCard title="Duration / Hour">
          <CallDurationChart data={stats?.callDurationByHour} />
        </ChartCard>
        <ChartCard title="Service Level">
          <ServiceLevelChart data={stats?.serviceLevel} />
        </ChartCard>
        <ChartCard title="Agent Status (Live)">
          <AgentStatusChart data={monitorData} />
        </ChartCard>
      </div>

      {/* Agent Summary — FIX: column headers match old PHP */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: '14px', boxShadow: 'var(--shadow)',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Agent Summary
          </h3>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['today', 'current_month', 'previous_month'].map(range => (
              <button
                key={range}
                onClick={() => setAgentDateRange(range)}
                style={{
                  padding: '4px 10px', fontSize: '11px', fontWeight: '500',
                  borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                  cursor: 'pointer', fontFamily: 'inherit',
                  background: agentDateRange === range ? 'var(--primary)' : 'var(--bg-body)',
                  color: agentDateRange === range ? 'white' : 'var(--text-secondary)'
                }}
              >
                {range === 'today' ? 'Today' : range === 'current_month' ? 'This Month' : 'Last Month'}
              </button>
            ))}
          </div>
        </div>

        {stats?.agentSummary?.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  {/* FIX: Match old PHP column headers */}
                  <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>Agent</th>
                  <th style={{ textAlign: 'right', padding: '8px', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>Total Calls</th>
                  <th style={{ textAlign: 'right', padding: '8px', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>Avg Ring Time (s)</th>
                </tr>
              </thead>
              <tbody>
                {stats.agentSummary.map((agent, i) => (
                  <tr
                    key={i}
                    style={{ borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}
                    onClick={() => setSelectedAgent(agent.agent)}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '8px', fontWeight: '500', color: 'var(--primary)', fontSize: '13px' }}>{agent.agent}</td>
                    <td style={{ textAlign: 'right', padding: '8px', fontVariantNumeric: 'tabular-nums' }}>{agent.total_calls}</td>
                    {/* FIX: Use avg_ring_time_seconds from backend */}
                    <td style={{ textAlign: 'right', padding: '8px', fontVariantNumeric: 'tabular-nums' }}>{agent.avg_ring_time_seconds}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>No agent data</div>
        )}
      </div>

      {/* Outbound Summary */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', padding: '14px', boxShadow: 'var(--shadow)'
      }}>
        <h3 style={{ margin: '0 0 10px', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Outbound Breakdown
        </h3>
        {stats?.outboundSummary?.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
                  <th style={{ textAlign: 'left', padding: '8px', fontWeight: '600', fontSize: '12px' }}>Extension</th>
                  <th style={{ textAlign: 'right', padding: '8px', fontWeight: '600', fontSize: '12px' }}>Calls</th>
                  <th style={{ textAlign: 'right', padding: '8px', fontWeight: '600', fontSize: '12px' }}>Talk (min)</th>
                </tr>
              </thead>
              <tbody>
                {stats.outboundSummary.map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      backgroundColor: i % 2 === 0 ? '#f9fafb' : 'white',
                      borderBottom: '1px solid var(--border-light)', cursor: 'pointer'
                    }}
                    onClick={() => {
                      const range = getDateRange(agentDateRange);
                      navigate(`/outbound-summary?extension=${row.extension}&start=${range.start}&end=${range.end}`);
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light)'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#f9fafb' : 'white'}
                  >
                    <td style={{ padding: '8px', fontWeight: '500', color: 'var(--primary)', fontSize: '13px' }}>{row.extension}</td>
                    <td style={{ textAlign: 'right', padding: '8px', fontVariantNumeric: 'tabular-nums' }}>{row.total_calls}</td>
                    <td style={{ textAlign: 'right', padding: '8px', fontVariantNumeric: 'tabular-nums' }}>{row.total_talk_time_minutes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>No outbound data</div>
        )}
      </div>

      {/* Modals */}
      {showMissedCalls && (
        <MissedCallsModal missedCalls={stats?.missedCalls} onClose={() => setShowMissedCalls(false)} />
      )}
      {selectedAgent && (
        <AgentDetailModal agent={selectedAgent} onClose={() => setSelectedAgent(null)} />
      )}
    </div>
  );
}

export default DashboardPage;

